import { type ApiError } from '@bizzdesign/sdk-internal';
import { type UUID } from '@bizzdesign/sdk-internal/uuid';
import { type Result } from '@bizzdesign/sdk-knowledgegraph';
import { type Observable } from 'rxjs';
import { type Result as TsResult } from 'ts-results';

export type {
  AsPagesOptions,
  Pages,
  Result,
} from '@bizzdesign/sdk-knowledgegraph';

/** The kind of frame/view element a comment thread is attached to. */
export type CommentParentType = 'FRAME' | 'VIEW';

/** Identifies the frame or view a comment thread is attached to. */
export type CommentParent = {
  type: CommentParentType;
  readonly id: UUID;
};

/** Position of a comment thread marker on its parent. */
export type CommentCoordinates = {
  x: number;
  y: number;
};

/** A user referenced by comment data, reactions, or read-state metadata. */
export type CommentUser = {
  readonly id: UUID;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
};

/** @deprecated Use CommentUser instead. */
export type CommentAuthor = CommentUser;

/** Creation/modification/hidden metadata common to comments and threads. */
export type CommentSystemAttributes = {
  createdAt: Date;
  createdBy?: CommentUser;
  updatedAt: Date;
  updatedBy?: CommentUser;
  hiddenAt?: Date;
  hiddenBy?: CommentUser;
  isHidden?: boolean;
};

/** Users who reacted to a comment with the same emoji value. */
export type CommentReaction = {
  emoji: string;
  users: CommentUser[];
};

/** A single comment within a comment thread. */
export type Comment = {
  readonly id: UUID;
  readonly threadId: UUID;
  text: string;
  author: CommentUser;
  reactions: CommentReaction[];
  readBy?: UUID[];
  systemAttributes: CommentSystemAttributes;
};

/** A comment thread anchored to a frame or view, with its replies. */
export type CommentThread = {
  readonly id: UUID;
  parentType: CommentParentType;
  readonly parentId: UUID;
  coordinates: CommentCoordinates;
  readonly graphicId?: UUID;
  resolved: boolean;
  resolvedAt?: Date;
  comments: Comment[];
  systemAttributes: CommentSystemAttributes;
};

/** Result of creating a new comment thread or replying to an existing one. */
export type CreateCommentThreadOutput = {
  comment: Comment;
  thread: CommentThread;
};

/** Result of updating a comment. */
export type UpdateCommentOutput = {
  comment: Comment;
  thread: CommentThread;
};

/** Result of deleting one or more comments. */
export type DeleteCommentsOutput = {
  comments: Comment[];
  threads: CommentThread[];
};

/** Result of updating a comment thread. */
export type UpdateCommentThreadOutput = {
  comment: Comment;
  thread: CommentThread;
};

/** Result of deleting one or more comment threads. */
export type DeleteCommentThreadsOutput = {
  threads: CommentThread[];
};

/** Result of resolving or reopening a comment thread. */
export type ResolveCommentThreadOutput = {
  thread: CommentThread;
};

/** Result of adding or removing a comment reaction. */
export type CommentReactionMutationOutput = {
  comment: Comment;
  thread: CommentThread;
};

/** Payload emitted when a comment is created. */
export type CommentCreatedPayload = {
  readonly threadId: UUID;
  parentType: CommentParentType | undefined;
  readonly parentId: UUID | undefined;
  coordinates: CommentCoordinates | undefined;
  readonly graphicId: UUID | undefined;
  resolved: boolean | undefined;
  resolvedAt: Date | undefined;
  threadSystemAttributes: CommentSystemAttributes | undefined;
  comment: Comment;
};

/** Payload emitted when a comment text changes. */
export type CommentUpdatedPayload = {
  readonly threadId: UUID;
  readonly commentId: UUID;
  text: string | undefined;
  systemAttributes: CommentSystemAttributes | undefined;
};

/** Payload emitted when comments are deleted from a thread. */
export type CommentsDeletedPayload = {
  readonly threadId: UUID;
  readonly commentIds: UUID[];
  deleteThread: boolean;
};

/** Payload emitted when a thread marker or root comment changes. */
export type CommentThreadUpdatedPayload = {
  readonly threadId: UUID;
  coordinates: CommentCoordinates | undefined;
  readonly graphicId: UUID | undefined;
  threadSystemAttributes: CommentSystemAttributes | undefined;
  readonly commentId: UUID | undefined;
  text: string | undefined;
  commentSystemAttributes: CommentSystemAttributes | undefined;
};

/** Payload emitted when a thread is resolved or reopened. */
export type CommentThreadResolvedPayload = {
  readonly threadId: UUID;
  resolved: boolean;
  resolvedAt: Date | undefined;
  systemAttributes: CommentSystemAttributes | undefined;
};

/** Payload emitted when a thread is deleted. */
export type CommentThreadDeletedPayload = {
  readonly threadId: UUID;
};

/** Payload emitted when a single user adds or removes a reaction. */
export type CommentReactionPayload = {
  readonly commentId: UUID;
  readonly threadId: UUID;
  emoji: string;
  user: CommentUser;
};

/** Real-time event emitted when a comment is created. */
export type CommentCreatedEvent = {
  readonly type: 'CREATE_COMMENT';
  payload: CommentCreatedPayload;
};

/** Real-time event emitted when a comment is updated. */
export type CommentUpdatedEvent = {
  readonly type: 'UPDATE_COMMENT';
  payload: CommentUpdatedPayload;
};

/** Real-time event emitted when comments are deleted. */
export type CommentsDeletedEvent = {
  readonly type: 'DELETE_COMMENT';
  payload: CommentsDeletedPayload;
};

/** Real-time event emitted when a comment thread is updated. */
export type CommentThreadUpdatedEvent = {
  readonly type: 'UPDATE_COMMENT_THREAD';
  payload: CommentThreadUpdatedPayload;
};

/** Real-time event emitted when a comment thread is resolved or reopened. */
export type CommentThreadResolvedEvent = {
  readonly type: 'RESOLVE_COMMENT_THREAD';
  payload: CommentThreadResolvedPayload;
};

/** Real-time event emitted when a comment thread is deleted. */
export type CommentThreadDeletedEvent = {
  readonly type: 'DELETE_COMMENT_THREAD';
  payload: CommentThreadDeletedPayload;
};

/** Real-time event emitted when a comment reaction is added. */
export type AddCommentReactionEvent = {
  readonly type: 'ADD_COMMENT_REACTION';
  payload: CommentReactionPayload;
};

/** Real-time event emitted when a comment reaction is removed. */
export type RemoveCommentReactionEvent = {
  readonly type: 'REMOVE_COMMENT_REACTION';
  payload: CommentReactionPayload;
};

/** Real-time comment event emitted by the chat subscription. */
export type CommentEvent =
  | CommentCreatedEvent
  | CommentUpdatedEvent
  | CommentsDeletedEvent
  | CommentThreadUpdatedEvent
  | CommentThreadResolvedEvent
  | CommentThreadDeletedEvent
  | AddCommentReactionEvent
  | RemoveCommentReactionEvent;

/** Comment subscription event types. */
export type CommentEventType = CommentEvent['type'];

/** All comment event types supported by the chat subscription. */
export const ALL_COMMENT_EVENT_TYPES = [
  'CREATE_COMMENT',
  'UPDATE_COMMENT',
  'DELETE_COMMENT',
  'UPDATE_COMMENT_THREAD',
  'DELETE_COMMENT_THREAD',
  'RESOLVE_COMMENT_THREAD',
  'ADD_COMMENT_REACTION',
  'REMOVE_COMMENT_REACTION',
] as const satisfies readonly CommentEventType[];

/** Filters comment subscription events. Defaults to all comment events. */
export type CommentEventFilter = {
  readonly eventTypes?: readonly CommentEventType[];
};

/** Criteria for fetching comment threads. */
export type CommentThreadsFilter = {
  parent: CommentParent;
  /** Optional text to search inside comments when selecting matching threads. */
  searchText?: string;
};

/** Criteria for fetching comments inside one thread. */
export type CommentsFilter = {
  /** The thread whose comments should be fetched. */
  readonly threadId: UUID;
  /** Optional text to search inside comments. */
  searchText?: string;
};

/** Minimal thread shape needed to fetch its comments. */
export type CommentThreadReference = Pick<CommentThread, 'id'>;

/** Optional filters when fetching comments from an existing thread object. */
export type GetCommentsForThreadOptions = {
  /** Optional text to search inside comments. */
  searchText?: string;
};

/** Field comment threads can be sorted by. */
export type CommentThreadSortField = 'createdAt' | 'updatedAt';
/** Sort direction. */
export type SortOrder = 'ASC' | 'DESC';

/** Sort order to apply when fetching comment threads. */
export type CommentThreadSort = {
  field: CommentThreadSortField;
  order: SortOrder;
};

/** Parameters for creating a new comment thread. */
export type CreateCommentThreadParams = {
  /** The frame or view the thread is attached to. */
  parent: CommentParent;
  /** The text of the first comment in the thread. */
  text: string;
  /** Where the thread marker is placed on the parent. */
  coordinates: CommentCoordinates;
  /** Optional id of the graphic element the thread is anchored to. */
  graphicId?: UUID;
};

/** Parameters for replying to an existing comment thread. */
export type ReplyToCommentThreadParams = {
  /** The thread to reply to. */
  threadId: UUID;
  /** The text of the reply. */
  text: string;
};

/** Parameters for updating a comment. */
export type UpdateCommentParams = {
  /** The comment to update. */
  id: UUID;
  /** Optional replacement text. */
  text?: string;
};

/** Parameters for deleting comments. */
export type DeleteCommentsParams = {
  /** The comments to delete. */
  ids: UUID[];
};

/** Parameters for updating a comment thread. */
export type UpdateCommentThreadParams = {
  /** The thread to update. */
  id: UUID;
  /** Optional replacement text for the thread root comment. */
  text?: string;
  /** Optional replacement marker coordinates. */
  coordinates?: CommentCoordinates;
  /** Optional replacement graphic id. */
  graphicId?: UUID;
};

/** Parameters for deleting comment threads. */
export type DeleteCommentThreadsParams = {
  /** The threads to delete. */
  ids: UUID[];
};

/** Parameters for resolving or reopening a comment thread. */
export type ResolveCommentThreadParams = {
  /** The thread to resolve or reopen. */
  id: UUID;
  /** Whether the thread should be resolved. */
  resolved: boolean;
};

/** Parameters for adding or removing a reaction on a comment. */
export type CommentReactionParams = {
  /** The comment to react to. */
  commentId: UUID;
  /** The emoji or reaction key to add/remove. */
  emoji: string;
};

/** Parameters for marking comments as read or unread. */
export type MarkCommentsParams = {
  /** Explicit comments to update. */
  commentIds: readonly UUID[] | undefined;
  /** Threads whose comments should all be updated. */
  threadIds: readonly UUID[] | undefined;
};

/** Result of marking comments as read or unread. */
export type MarkCommentsOutput = {
  commentIds: UUID[];
};

export type ChatClient = {
  /**
   * Creates a new comment thread on a frame or a view.
   *
   * @param params Thread parent, first comment text, marker position, and optional graphic id.
   */
  createCommentThread(
    params: CreateCommentThreadParams,
  ): Promise<TsResult<CreateCommentThreadOutput, ApiError>>;

  /**
   * Adds a reply to an existing comment thread. The parent is deduced
   * server-side from the thread, so it does not need to be provided again.
   *
   * @param params The thread to reply to and the reply text.
   */
  replyToCommentThread(
    params: ReplyToCommentThreadParams,
  ): Promise<TsResult<CreateCommentThreadOutput, ApiError>>;

  /**
   * Updates a comment.
   *
   * @param params The comment id and replacement text.
   */
  updateComment(
    params: UpdateCommentParams,
  ): Promise<TsResult<UpdateCommentOutput, ApiError>>;

  /**
   * Deletes one or more comments.
   *
   * @param params The ids of the comments to delete.
   */
  deleteComments(
    params: DeleteCommentsParams,
  ): Promise<TsResult<DeleteCommentsOutput, ApiError>>;

  /**
   * Updates a comment thread, including its marker fields or root text.
   *
   * @param params The thread id and fields to update.
   */
  updateCommentThread(
    params: UpdateCommentThreadParams,
  ): Promise<TsResult<UpdateCommentThreadOutput, ApiError>>;

  /**
   * Deletes one or more comment threads.
   *
   * @param params The ids of the threads to delete.
   */
  deleteCommentThreads(
    params: DeleteCommentThreadsParams,
  ): Promise<TsResult<DeleteCommentThreadsOutput, ApiError>>;

  /**
   * Resolves or reopens a comment thread.
   *
   * @param params The thread id and desired resolved state.
   */
  resolveCommentThread(
    params: ResolveCommentThreadParams,
  ): Promise<TsResult<ResolveCommentThreadOutput, ApiError>>;

  /**
   * Marks comments as read for the calling user.
   *
   * @param params Explicit comment ids and/or thread ids whose comments should be marked as read.
   * @returns The ids of the comments whose read state was updated.
   * @remarks Passing a thread id marks all comments in that thread as read.
   */
  markCommentsAsRead(
    params: MarkCommentsParams,
  ): Promise<TsResult<MarkCommentsOutput, ApiError>>;

  /**
   * Marks comments as unread for the calling user.
   *
   * @param params Explicit comment ids and/or thread ids whose comments should be marked as unread.
   * @returns The ids of the comments whose read state was updated.
   * @remarks Passing a thread id marks all comments in that thread as unread.
   */
  markCommentsAsUnread(
    params: MarkCommentsParams,
  ): Promise<TsResult<MarkCommentsOutput, ApiError>>;

  /**
   * Adds a reaction to a comment.
   *
   * @param params The target comment id and reaction value.
   */
  addCommentReaction(
    params: CommentReactionParams,
  ): Promise<TsResult<CommentReactionMutationOutput, ApiError>>;

  /**
   * Removes a reaction from a comment.
   *
   * @param params The target comment id and reaction value.
   */
  removeCommentReaction(
    params: CommentReactionParams,
  ): Promise<TsResult<CommentReactionMutationOutput, ApiError>>;

  /**
   * Subscribes to real-time comment events.
   *
   * @param filter Optional event type filter. When omitted, all comment, thread, and reaction events are emitted.
   * @returns An RxJS Observable that emits minimal comment payloads for client-side patching.
   */
  onCommentEvents$(filter?: CommentEventFilter): Observable<CommentEvent>;

  /**
   * Returns whether comment content operations are locked for this thread.
   * Locked threads reject replies, comment edits/deletes, thread metadata edits, and reactions.
   * Deleting the thread itself and reopening it are still allowed.
   *
   * @param thread The thread to inspect.
   */
  isCommentThreadLocked(thread: CommentThread): boolean;

  /**
   * Fetches comment threads (and their comments) for a given parent. Threads can
   * accumulate quickly on a busy frame or view, so the result supports streaming
   * (`stream()`), random-access pagination (`asPages()`), and counting (`getCount()`)
   * without requiring the full collection to be loaded upfront.
   *
   * @param filter Filter identifying the parent (frame or view) to fetch threads for.
   * @param orderBy Optional sort order.
   */
  getCommentThreads(
    filter: CommentThreadsFilter,
    orderBy?: CommentThreadSort,
  ): Result<CommentThread>;

  /**
   * Fetches comments inside a single thread. Comments can grow quickly in long
   * discussions, so the result supports streaming (`stream()`), random-access
   * pagination (`asPages()`), and counting (`getCount()`) without requiring the
   * full thread to be loaded upfront.
   *
   * @param filter Filter identifying the thread and optional comment text search.
   */
  getComments(filter: CommentsFilter): Result<Comment>;

  /**
   * Fetches comments for an existing thread object.
   *
   * @param thread Thread object, or any object carrying a thread `id`.
   * @param options Optional text search applied inside that thread's comments.
   * @returns A paginated {@link Result} of comments for the thread.
   */
  getCommentsForThread(
    thread: CommentThreadReference,
    options?: GetCommentsForThreadOptions,
  ): Result<Comment>;
};
